import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  effect,
  inject,
  viewChild,
} from '@angular/core';

import { createFocusTrap } from '../../utils/focus-trap';
import { ButtonComponent } from '../../components/button/button.component';
import type { UiDialogComponentPayload } from '../../services/dialog.service';
import { DialogService } from '../../services/dialog.service';

@Component({
  selector: 'app-ui-modal-host',
  standalone: true,
  imports: [NgComponentOutlet, ButtonComponent],
  template: `
    @if (dialog.state(); as s) {
      <div
        class="ui-modal-backdrop"
        role="presentation"
        (click)="onBackdrop($event)"
        [attr.aria-hidden]="true"
      >
        <div
          #panel
          class="ui-modal"
          [class.ui-modal--sm]="s.size === 'sm'"
          [class.ui-modal--md]="s.size === 'md'"
          [class.ui-modal--lg]="s.size === 'lg'"
          role="dialog"
          [attr.aria-modal]="true"
          [attr.aria-labelledby]="titleId"
          (click)="$event.stopPropagation()"
        >
          <header class="ui-modal__head">
            <h2 class="ui-modal__title" [id]="titleId">{{ s.title }}</h2>
            @if (s.closable) {
              <button type="button" class="ui-modal__x" (click)="dialog.close()" aria-label="Close dialog">
                ×
              </button>
            }
          </header>
          <div class="ui-modal__body">
            @if (s.kind === 'text') {
              <p>{{ s.message }}</p>
            } @else {
              <ng-container *ngComponentOutlet="s.component; inputs: componentInputs(s)" />
            }
          </div>
          @if (s.kind === 'text') {
            <footer class="ui-modal__foot">
              <app-button type="button" variant="primary" (clicked)="dialog.close()">OK</app-button>
            </footer>
          }
        </div>
      </div>
    }
  `,
  styles: `
    :host {
      display: contents;
    }
    .ui-modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: var(--ui-z-modal-backdrop);
      background: rgba(0, 0, 0, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--ui-space-4);
    }
    .ui-modal {
      z-index: var(--ui-z-modal);
      width: 100%;
      max-height: min(90vh, 720px);
      overflow: auto;
      background: var(--ui-color-surface);
      color: var(--ui-color-text);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-md);
      border: 1px solid var(--ui-color-border);
    }
    .ui-modal--sm {
      max-width: 24rem;
    }
    .ui-modal--md {
      max-width: 36rem;
    }
    .ui-modal--lg {
      max-width: 48rem;
    }
    .ui-modal__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--ui-space-3);
      padding: var(--ui-space-4);
      border-bottom: 1px solid var(--ui-color-border);
    }
    .ui-modal__title {
      margin: 0;
      font-size: var(--ui-font-size-lg);
    }
    .ui-modal__x {
      border: none;
      background: transparent;
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      color: var(--ui-color-text-muted);
    }
    .ui-modal__x:focus-visible {
      outline: 2px solid var(--ui-color-primary);
      border-radius: var(--ui-radius-sm);
    }
    .ui-modal__body {
      padding: var(--ui-space-4);
      font-size: var(--ui-font-size-sm);
    }
    .ui-modal__foot {
      padding: var(--ui-space-4);
      border-top: 1px solid var(--ui-color-border);
      display: flex;
      justify-content: flex-end;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiModalHostComponent {
  protected readonly dialog = inject(DialogService);

  protected readonly titleId = `ui-modal-title-${Math.random().toString(36).slice(2, 9)}`;

  protected componentInputs(s: UiDialogComponentPayload): Record<string, unknown> {
    return s.inputs ?? {};
  }

  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  constructor() {
    effect((onCleanup) => {
      if (!this.dialog.state()) {
        return;
      }
      let release: (() => void) | undefined;
      const handle = window.setTimeout(() => {
        const el = this.panel()?.nativeElement;
        if (el) {
          release = createFocusTrap(el);
        }
      }, 0);
      onCleanup(() => {
        window.clearTimeout(handle);
        release?.();
      });
    });
  }

  protected onBackdrop(event: MouseEvent): void {
    const s = this.dialog.state();
    if (s?.closable && event.target === event.currentTarget) {
      this.dialog.close();
    }
  }

  @HostListener('document:keydown', ['$event'])
  protected onDocKeydown(event: KeyboardEvent): void {
    const s = this.dialog.state();
    if (event.key === 'Escape' && s?.closable) {
      event.preventDefault();
      this.dialog.close();
    }
  }
}
